import type { DashboardRow, DashboardSlot } from "wasp/entities";
import { WIDGET_REGISTRY } from "./registry";
import useTranslation from "../../client/hooks/useTranslation";

type RowWithSlots = DashboardRow & { slots: DashboardSlot[] };

interface LayoutPreviewGridProps {
  rows: RowWithSlots[];
  className?: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  dashboard: "bg-teal-500/20 border-teal-500/30 text-teal-300",
  pfm:       "bg-orange-500/20 border-orange-500/30 text-orange-300",
  both:      "bg-blue-500/20 border-blue-500/30 text-blue-300",
};

const WIDGET_LABELS: Record<string, string> = {
  "net-pnl": "Net PnL",
  "profit-factor": "Profit Factor",
  "win-rate": "Win Rate",
  "avg-win-loss": "Avg Win/Loss",
  "equity-chart": "Equity Chart",
  "accounts-table": "Accounts",
  "analytics-kpis": "Analytics KPIs",
  "equity-curve": "Equity Curve",
  "pnl-distribution": "PnL Distribution",
  "monthly-pnl": "Monthly PnL",
  "symbol-performance": "Symbol Perf.",
  "rolling-win-rate": "Rolling Win Rate",
  "heatmap": "Heatmap",
  "behavioral-biases": "Biases",
  "session-time-slots": "Time Slots",
  "session-mood": "Session Mood",
  "session-duration": "Duration",
  "outside-session": "Outside Session",
  "session-insight": "Session Insight",
  "sessions-list": "Sessions",
  "pip-stats": "Pip Stats",
  "pip-distribution": "Pip Distribution",
  "pip-by-symbol": "Pip/Symbol",
  "calendar": "Calendar",
  "market-hours": "Market Hours",
  "routines": "Routines",
  "goals": "Goals",
  "session": "Session",
  "note": "Note",
  "mentor-notes": "Mentor Notes",
  "propfirm-kpis": "PF KPIs",
  "propfirm-calendar": "PF Calendar",
  "propfirm-month-recap": "Month Recap",
  "propfirm-reporting": "Reporting",
  "propfirm-accounts": "PF Accounts",
  "propfirm-payouts-list": "Payouts",
  "propfirm-filters": "PF Filters",
  "propfirm-accounts-table": "Accounts Table",
  "propfirm-global-charges": "Charges",
  "propfirm-wallet-balance": "Wallet",
  "propfirm-wallet-transfers": "Transfers",
  "propfirm-wallet-withdrawals": "Withdrawals",
  "news-feed": "News",
  "interest-rates": "Interest Rates",
  "economic-calendar": "Economic Cal.",
  "earnings-calendar": "Earnings Cal.",
  "chart": "Chart",
  "topbar-date-filter": "Date",
  "topbar-account-filter": "Account",
  "topbar-strategy-filter": "Strategy",
  "topbar-add-trade": "Add Trade",
  "topbar-add-payout": "Add Payout",
  "topbar-pfm-filter": "PF Filter",
  "topbar-phase-filter": "Phase",
};

export default function LayoutPreviewGrid({ rows, className = "" }: LayoutPreviewGridProps) {
  const t = useTranslation();
  // Filter out topbar rows (order === 0) — topbar widgets are not shown in the preview grid
  const gridRows = rows.filter((r) => r.order > 0).sort((a, b) => a.order - b.order);
  const allSlots = gridRows.flatMap((r) => r.slots.map((s) => ({ ...s, globalRow: r.order })));

  if (allSlots.length === 0) {
    return (
      <div className={`flex items-center justify-center h-48 rounded-lg border border-dashed border-border text-sm text-muted-foreground ${className}`}>
        {t.dashboard.layouts.previewEmpty}
      </div>
    );
  }

  const maxRow = Math.max(...allSlots.map((s) => s.globalRow + s.rowSpan - 1));
  // Compute row height so the full layout fits within MAX_PREVIEW_HEIGHT
  const MAX_PREVIEW_HEIGHT = 320;
  const PADDING = 8;
  const GAP = 2;
  const rowHeight = Math.floor((MAX_PREVIEW_HEIGHT - PADDING - GAP * (maxRow - 1)) / maxRow);
  const actualHeight = rowHeight * maxRow + GAP * (maxRow - 1) + PADDING;

  return (
    <div
      className={`relative w-full rounded-lg overflow-hidden border border-border/40 bg-card/30 ${className}`}
    >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gridTemplateRows: `repeat(${maxRow}, ${rowHeight}px)`,
        height: `${actualHeight}px`,
        gap: `${GAP}px`,
        padding: "4px",
      }}
    >
      {allSlots.map((slot) => {
        const reg = WIDGET_REGISTRY[slot.widgetKey as keyof typeof WIDGET_REGISTRY];
        const scope = reg?.scope ?? "both";
        const colorClass = CATEGORY_COLORS[scope] ?? CATEGORY_COLORS["both"];
        const label = WIDGET_LABELS[slot.widgetKey]
          ?? (t.dashboard.widgets as Record<string, string>)[slot.widgetKey.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())]
          ?? slot.widgetKey;

        return (
          <div
            key={slot.id}
            className={`rounded flex items-center justify-center border text-[9px] font-medium overflow-hidden px-1 ${colorClass}`}
            style={{
              gridColumn: `${slot.colStart} / span ${slot.colSpan}`,
              gridRow: `${slot.globalRow} / span ${slot.rowSpan}`,
            }}
            title={label}
          >
            <span className="truncate">{label}</span>
          </div>
        );
      })}
    </div>
    </div>
  );
}
